import { useCallback, useMemo, useState } from 'react';
import { updateUserProfile } from '../services/profileService';
import {
    buildUpdatePayload,
    describeUpdateError,
    hasProfileChanges,
    toFormValues,
    validateProfileForm,
} from '../utils/profileValidation';

/**
 * The edit form for the profile: values, checks, and saving with PATCH /rfp/auth/users/{user_id}.
 * Mount it only while editing, so every edit session starts from the current profile.
 *
 * @param {{ profile: object, userId: string, onSaved: (updatedProfile: object) => void }} options
 *        onSaved receives the server's answer (the full updated profile)
 */
export const useProfileEdit = ({ profile, userId, onSaved }) => {
    const initial = useMemo(() => toFormValues(profile), [profile]);
    const [values, setValues] = useState(initial);
    const [fieldErrors, setFieldErrors] = useState({});
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);

    const isDirty = useMemo(() => hasProfileChanges(values, initial), [values, initial]);

    const setField = useCallback((key, value) => {
        setValues((previous) => ({ ...previous, [key]: value }));
        // The message for a field goes away as soon as the user starts fixing it.
        setFieldErrors((previous) => {
            if (!previous[key]) return previous;
            const { [key]: removed, ...rest } = previous;
            return rest;
        });
        setFormError('');
    }, []);

    const save = useCallback(async () => {
        if (saving) return;

        const problems = validateProfileForm(values);
        if (Object.keys(problems).length > 0) {
            setFieldErrors(problems);
            setFormError('Please fix the highlighted fields.');
            return;
        }

        setSaving(true);
        setFormError('');
        setFieldErrors({});
        try {
            const response = await updateUserProfile(userId, buildUpdatePayload(values));
            if (onSaved) onSaved(response?.data);
        } catch (error) {
            const described = describeUpdateError(error);
            setFieldErrors(described.fieldErrors);
            setFormError(described.message);
        } finally {
            setSaving(false);
        }
        // On success the page leaves edit mode, which unmounts this form.
    }, [saving, values, userId, onSaved]);

    return { values, fieldErrors, formError, isDirty, saving, setField, save };
};
